import { createLogger } from "@chesspresso/common";
import { formatDuration, intervalToDuration } from "date-fns";

const logger = createLogger("time-control-util");

export type BaseTimeControl = {
    type: "base";
    baseSeconds: number;
    representation: string;
    raw: string;
};
export type BaseIncrementTimeControl = {
    type: "base+increment";
    baseSeconds: number;
    incrementSeconds: number;
    representation: string;
    raw: string;
};
export type MovesPerPeriodTimeControl = {
    type: "movesPerPeriod";
    moves: number;
    periodSeconds: number;
    representation: string;
    raw: string;
};
export type PerMoveTimeControl = {
    type: "perMove";
    moves: number;
    periodSeconds: number;
    representation: string;
    raw: string;
};

export type ChessTimeControl =
    | BaseTimeControl
    | BaseIncrementTimeControl
    | MovesPerPeriodTimeControl
    | PerMoveTimeControl;

const formatSeconds = (seconds: number): string => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
    return formatDuration(duration, { delimiter: " " }) || "0 seconds";
};

const parse = (tc: string): ChessTimeControl | undefined => {
    // Base time + increment: "600+5"
    const baseIncMatch = tc.match(/^(\d+)\+(\d+)$/);
    if (baseIncMatch) {
        const baseSeconds = Number(baseIncMatch[1]);
        const incrementSeconds = Number(baseIncMatch[2]);
        return {
            type: "base+increment",
            baseSeconds,
            incrementSeconds,
            representation: `${formatSeconds(baseSeconds)} + ${formatSeconds(incrementSeconds)}`,
            raw: tc,
        };
    }

    // Moves per period: "40/7200"
    const movesPeriodMatch = tc.match(/^(\d+)\/(\d+)$/);
    if (movesPeriodMatch) {
        const moves = Number(movesPeriodMatch[1]);
        const periodSeconds = Number(movesPeriodMatch[2]);
        return {
            type: "movesPerPeriod",
            moves,
            periodSeconds,
            representation: `${moves} ${moves > 1 ? "moves" : "move"} in ${formatSeconds(periodSeconds)}`,
            raw: tc,
        };
    }

    // Per move/correspondence: "1/259200"
    const perMoveMatch = tc.match(/^1\/(\d+)$/);
    if (perMoveMatch) {
        const periodSeconds = Number(perMoveMatch[1]);
        return {
            type: "perMove",
            moves: 1,
            periodSeconds,
            representation: `1 move every ${formatSeconds(periodSeconds)}`,
            raw: tc,
        };
    }

    // Base time only: "600"
    const baseMatch = tc.match(/^(\d+)$/);
    if (baseMatch) {
        const baseSeconds = Number(baseMatch[1]);
        return {
            type: "base",
            baseSeconds,
            representation: formatSeconds(baseSeconds),
            raw: tc,
        };
    }

    return undefined;
};

const parseChessComTimeControlMs = (rawTime: string): number | undefined => {
    try {
        const currentTimeControl = rawTime.trim();

        if (!currentTimeControl) {
            return undefined;
        }

        if (currentTimeControl.includes("/")) {
            const [, secondsPerMoveStr] = currentTimeControl.split("/");
            const secondsPerMove = Number(secondsPerMoveStr);

            if (!Number.isFinite(secondsPerMove) || secondsPerMove <= 0) {
                return undefined;
            }

            const milliSecondsPerMove = secondsPerMove * 1000;

            return milliSecondsPerMove;
        }

        const baseSeconds = Number(currentTimeControl.split("+")[0]);

        if (!Number.isFinite(baseSeconds) || baseSeconds < 0) {
            return undefined;
        }

        const milliSeconds = baseSeconds * 1000;

        return milliSeconds;
    } catch {
        logger.error(`Invalid time format: ${rawTime}`);
        return undefined;
    }
};

const parseChessComClockTimeMs = (rawTime?: string): number | undefined => {
    if (!rawTime) {
        return undefined;
    }

    const time = rawTime.trim();
    if (!time) {
        return undefined;
    }

    const hms = time.match(/^(\d+):(\d{1,2}):(\d{2})(?:\.(\d+))?$/);
    if (hms) {
        const [, h, m, s, frac = "0"] = hms;
        return (
            Number(h) * 3_600_000 +
            Number(m) * 60_000 +
            Number(s) * 1_000 +
            Number(frac.padEnd(3, "0").slice(0, 3))
        );
    }

    const milliSeconds = time.match(/^(\d{1,2}):(\d{2})(?:\.(\d+))?$/);
    if (milliSeconds) {
        const [, m, s, frac = "0"] = milliSeconds;
        return (
            Number(m) * 60_000 +
            Number(s) * 1_000 +
            Number(frac.padEnd(3, "0").slice(0, 3))
        );
    }

    const seconds = time.match(/^(\d+)(?:\.(\d+))?$/);
    if (seconds) {
        const [, sec, frac = "0"] = seconds;
        return Number(sec) * 1_000 + Number(frac.padEnd(3, "0").slice(0, 3));
    }

    return undefined;
};

export const TimeControlUtil = {
    parse,
    parseChessComTimeControlMs,
    parseChessComClockTimeMs,
};
